import React from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import {
  Plus,
  Pencil,
  Move,
  Trash2,
  UserPlus,
  UserMinus,
  Hand,
  CheckCircle,
  XCircle,
  Clock,
  X,
  User,
  ArrowRight,
  ExternalLink,
  type LucideIcon,
} from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/core/utils';
import type { ShiftSchedulingActivityEventWithDetails } from '@/types/shift-scheduling-activity';
import { SCHEDULING_EVENT_CONFIG } from '@/types/shift-scheduling-activity';

interface ShiftSchedulingLogDetailPanelProps {
  event: ShiftSchedulingActivityEventWithDetails;
  onClose: () => void;
  onShiftClick?: (shiftId: string, shiftDate: Date) => void;
}

const ICON_MAP: Record<string, LucideIcon> = {
  Plus,
  Pencil,
  Move,
  Trash2,
  UserPlus,
  UserMinus,
  Hand,
  CheckCircle,
  XCircle,
  Clock,
};

export const ShiftSchedulingLogDetailPanel: React.FC<ShiftSchedulingLogDetailPanelProps> = ({
  event,
  onClose,
  onShiftClick,
}) => {
  const config = SCHEDULING_EVENT_CONFIG[event.event_type];
  const Icon = ICON_MAP[config.icon] || Clock;
  const eventTime = new Date(event.event_timestamp);

  // Get the target employee (who was affected)
  const targetEmployee = event.target_employee;
  const targetInitials = targetEmployee?.name
    ? targetEmployee.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : '??';

  // Get the actor (who made the change)
  const actorEmployee = event.actor_employee;
  const actorInitials = actorEmployee?.name
    ? actorEmployee.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : null;

  // Format shift time
  const shiftTimeDisplay = event.shift_start_time && event.shift_end_time
    ? `${format(new Date(event.shift_start_time), 'h:mm a')} - ${format(new Date(event.shift_end_time), 'h:mm a')}`
    : null;

  // Shift click handler
  const hasShift = !!event.shift_id;
  const shiftDate = event.shift_date ? new Date(event.shift_date) : eventTime;

  const handleShiftClick = () => {
    if (hasShift && onShiftClick && event.shift_id) {
      onShiftClick(event.shift_id, shiftDate);
    }
  };

  return (
    <div className="h-full flex flex-col bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b">
        <h3 className="text-sm font-medium">Event Details</h3>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4 space-y-4">
        {/* Event Type Header */}
        <div className="flex items-center gap-2">
          <Icon className={cn('h-5 w-5', config.color)} />
          <span className="font-medium">{config.label}</span>
          {event.actor_type === 'employee' && (
            <Badge variant="outline" className="text-xs">
              Self-service
            </Badge>
          )}
        </div>

        {/* Author/Actor Section - who made the change */}
        <div className="p-3 rounded-lg bg-muted/50 space-y-2">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Changed by</p>
          <div className="flex items-center gap-2">
            {actorEmployee ? (
              <>
                <Avatar className="h-6 w-6">
                  <AvatarImage src={actorEmployee.avatar_url || undefined} />
                  <AvatarFallback className="text-xs">{actorInitials}</AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium">{actorEmployee.name}</span>
              </>
            ) : (
              <>
                <div className="h-6 w-6 rounded-full bg-muted flex items-center justify-center">
                  <User className="h-3 w-3 text-muted-foreground" />
                </div>
                <span className="text-sm text-muted-foreground">
                  {event.actor_type === 'system' ? 'System' : 'Unknown'}
                </span>
              </>
            )}
          </div>
        </div>

        {/* Target Employee - who was affected (if different from actor) */}
        {targetEmployee && targetEmployee.id !== actorEmployee?.id && (
          <div className="p-3 rounded-lg bg-muted/50 space-y-2">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Affected Employee</p>
            <div className="flex items-center gap-2">
              <Avatar className="h-6 w-6">
                <AvatarImage src={targetEmployee.avatar_url || undefined} />
                <AvatarFallback className="text-xs">{targetInitials}</AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium">{targetEmployee.name}</span>
            </div>
          </div>
        )}

        <Separator />

        {/* Timestamp */}
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Time</p>
          <p className="text-sm">
            {format(eventTime, 'EEEE, MMMM d, yyyy')}
          </p>
          <p className="text-lg font-semibold tabular-nums">
            {format(eventTime, 'h:mm:ss a')}
          </p>
          <p className="text-xs text-muted-foreground">
            {formatDistanceToNow(eventTime, { addSuffix: true })}
          </p>
        </div>

        {/* Shift Info - Clickable */}
        {(event.shift_title || shiftTimeDisplay) && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Shift</p>
              <button
                onClick={handleShiftClick}
                disabled={!hasShift || !onShiftClick}
                className={cn(
                  'w-full text-left p-2 rounded-lg transition-colors',
                  hasShift && onShiftClick
                    ? 'hover:bg-primary/10 cursor-pointer group'
                    : 'cursor-default'
                )}
              >
                <div className="flex items-center justify-between">
                  <div>
                    {event.shift_title && (
                      <p className="text-sm font-medium">{event.shift_title}</p>
                    )}
                    {shiftTimeDisplay && (
                      <p className="text-sm text-muted-foreground">{shiftTimeDisplay}</p>
                    )}
                    {event.shift_date && (
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(event.shift_date), 'EEEE, MMMM d')}
                      </p>
                    )}
                  </div>
                  {hasShift && onShiftClick && (
                    <ExternalLink className="h-4 w-4 text-muted-foreground group-hover:text-primary" />
                  )}
                </div>
              </button>
            </div>
          </>
        )}

        {/* Event-specific details */}
        {event.event_type === 'shift_moved' && event.metadata && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Move Details</p>
              <div className="p-2 rounded bg-purple-50 dark:bg-purple-900/20 text-sm">
                {event.metadata.previousStart && event.metadata.newStart && (
                  <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                    <span>{format(new Date(event.metadata.previousStart), 'MMM d, h:mm a')}</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                    <span className="font-medium">{format(new Date(event.metadata.newStart), 'MMM d, h:mm a')}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {event.event_type === 'shift_edited' && event.metadata?.changes && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Changes</p>
              <div className="space-y-1 text-sm">
                {event.metadata.changes.title && (
                  <div>
                    <span className="text-muted-foreground">Title: </span>
                    <span className="line-through text-muted-foreground">{event.metadata.changes.title.old || 'None'}</span>
                    <span className="mx-1">→</span>
                    <span className="font-medium">{event.metadata.changes.title.new || 'None'}</span>
                  </div>
                )}
                {event.metadata.changes.notes && (
                  <div>
                    <span className="text-muted-foreground">Notes updated</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {(event.event_type === 'employee_assigned' || event.event_type === 'employee_unassigned') && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                {event.event_type === 'employee_assigned' ? 'Assignment' : 'Unassignment'}
              </p>
              <div className="p-2 rounded bg-indigo-50 dark:bg-indigo-900/20 text-sm">
                {/* Show which shift */}
                <div className="text-indigo-700 dark:text-indigo-300 mb-2">
                  <p className="font-medium">{event.shift_title || 'Untitled Shift'}</p>
                  {shiftTimeDisplay && (
                    <p className="text-xs">{shiftTimeDisplay}</p>
                  )}
                  {event.shift_date && (
                    <p className="text-xs opacity-75">
                      {format(new Date(event.shift_date), 'EEEE, MMMM d')}
                    </p>
                  )}
                </div>
                {/* Show reassignment info if applicable */}
                {event.metadata?.previousEmployeeName && (
                  <div className="flex items-center gap-2 pt-2 border-t border-indigo-200 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300">
                    <span>{event.metadata.previousEmployeeName}</span>
                    <ArrowRight className="h-3.5 w-3.5" />
                    <span className="font-medium">{event.target_employee?.name || event.metadata.newEmployeeName}</span>
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Request notes */}
        {(event.event_type === 'employee_self_requested' ||
          event.event_type === 'employee_request_approved' ||
          event.event_type === 'employee_request_rejected') &&
          (event.metadata?.employeeNotes || event.metadata?.managerNotes) && (
            <>
              <Separator />
              <div className="space-y-2">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Notes</p>
                {event.metadata.employeeNotes && (
                  <div className="p-2 rounded bg-muted text-sm">
                    <p className="text-xs text-muted-foreground mb-1">Employee:</p>
                    <p className="italic">{event.metadata.employeeNotes}</p>
                  </div>
                )}
                {event.metadata.managerNotes && (
                  <div className="p-2 rounded bg-muted text-sm">
                    <p className="text-xs text-muted-foreground mb-1">Manager:</p>
                    <p className="italic">{event.metadata.managerNotes}</p>
                  </div>
                )}
              </div>
            </>
          )}

        {/* Source info for shift_created */}
        {event.event_type === 'shift_created' && event.metadata?.source && (
          <>
            <Separator />
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Source</p>
              <Badge variant="outline" className="text-xs capitalize">
                {event.metadata.source.replace('-', ' ')}
              </Badge>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShiftSchedulingLogDetailPanel;
